// ─── Languages Modal ──────────────────────────────────────────
// Multi-language config for invitation:
//  - enable/disable languages
//  - choose default language
//  - auto-translate a non-default language using LLM
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, Check, Sparkles, X, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { LANGUAGE_OPTIONS } from '../../lib/i18nInvite';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LanguagesPanel({
  eventId,
  languages = [],
  defaultLanguage = 'en',
  hasTranslation = {},   // { it: true, pt: false }  — which languages have a saved overlay
  translationGeneratedAt = {},  // { it: '2026-05-29T...', pt: null } — ISO when each overlay was generated
  contentUpdatedAt = null,      // ISO of the rsvp_page's last content edit
  onSaved,               // ({ languages, default_language }) => void
  onTranslated,          // (target) => void   parent should refetch rsvp_page to pull v2_translations
  onClose,
}) {
  const [enabled, setEnabled] = useState(new Set(languages.length ? languages : [defaultLanguage]));
  const [defLang, setDefLang] = useState(defaultLanguage || 'en');
  const [saving, setSaving] = useState(false);
  const [translatingLang, setTranslatingLang] = useState(null);

  // ─── Translations Health (Lang-A.8) ────────────────────────
  // Detect legacy structural pollution in stored language overlays
  // (id/layout/src etc. that should have stayed canonical).
  // Auto-scans on mount; designer can clean with one click.
  const [health, setHealth] = useState(null); // { total_stale_fields, languages_affected, healthy }
  const [cleaning, setCleaning] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/translations/health`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setHealth(res.data);
    } catch {
      setHealth(null); // best-effort — never block the panel
    }
  }, [eventId]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const handleCleanTranslations = async () => {
    setCleaning(true);
    try {
      const res = await axios.post(
        `${API}/events/${eventId}/translations/clean`,
        null,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const removed = res.data?.fields_removed ?? 0;
      const langs = res.data?.languages_cleaned ?? 0;
      toast.success(
        removed > 0
          ? `Cleaned ${removed} stale field${removed === 1 ? '' : 's'} across ${langs} language${langs === 1 ? '' : 's'}.`
          : 'Translations already healthy.',
      );
      await fetchHealth();
      // Tell the parent to refetch so the in-memory overlay matches
      // what's now persisted (canonical layout, no pollution).
      onTranslated && onTranslated('__cleanup__');
    } catch (e) {
      toast.error('Cleanup failed — please try again.');
    } finally {
      setCleaning(false);
    }
  };

  const toggleLang = (code) => {
    const next = new Set(enabled);
    if (next.has(code)) {
      if (code === defLang) {
        toast.error('Disable the default language by selecting another default first.');
        return;
      }
      next.delete(code);
    } else {
      next.add(code);
    }
    setEnabled(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const langs = Array.from(enabled);
      if (!langs.includes(defLang)) langs.push(defLang);
      await axios.put(
        `${API}/events/${eventId}/languages`,
        { languages: langs, default_language: defLang },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success('Languages updated');
      onSaved && onSaved({ languages: langs, default_language: defLang });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save languages');
    } finally {
      setSaving(false);
    }
  };

  const handleTranslate = async (target) => {
    // Ensure default + target are saved first
    setTranslatingLang(target);
    // Phase Lang-A.5b — capture start time so the post-error poll knows whether
    // the overlay's _generated_at was written DURING this run (vs. left over
    // from an earlier successful run).
    const runStartMs = Date.now();
    try {
      const langs = Array.from(enabled);
      if (!langs.includes(target)) langs.push(target);
      if (!langs.includes(defLang)) langs.push(defLang);
      await axios.put(
        `${API}/events/${eventId}/languages`,
        { languages: langs, default_language: defLang },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const res = await axios.post(
        `${API}/events/${eventId}/invitation/translate?target=${target}`,
        null,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          // Phase Lang-A.5 — LLM translation can legitimately take 20–40s for
          // a large invitation; give the client more headroom before axios
          // aborts. The K8s ingress timeout is the harder limit and the
          // catch-block below additionally polls the overlay if the gateway
          // gives up.
          timeout: 90_000,
        },
      );
      toast.success(`Translated ${res.data?.translated_count || 0} strings to ${target.toUpperCase()}`);
      onSaved && onSaved({ languages: res.data.languages, default_language: res.data.default_language });
      onTranslated && onTranslated(target);
    } catch (e) {
      // Phase Lang-A.5b — false-positive guard for gateway timeouts.
      // For a large invitation, the LLM round-trip can exceed the K8s
      // ingress timeout (~30s). The backend still completes and writes
      // the overlay AFTER the gateway has already returned 504 to us.
      // We poll up to 8 times at 5s intervals (40s window) and treat any
      // _generated_at stamped after runStart as a successful run.
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail || e?.message || 'Translation failed';
      // eslint-disable-next-line no-console
      console.error('[LanguagesPanel] re-translate request error:', { status, detail, error: e });

      let confirmedSuccess = false;
      const POLL_ATTEMPTS = 8;
      const POLL_INTERVAL_MS = 5_000;
      for (let attempt = 0; attempt < POLL_ATTEMPTS && !confirmedSuccess; attempt += 1) {
        try {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          const fresh = await axios.get(
            `${API}/events/${eventId}/rsvp-page`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
          );
          const overlay = (fresh.data?.v2_translations || {})[target];
          const genAt = overlay?._generated_at;
          if (genAt) {
            const genMs = new Date(genAt).getTime();
            // Stamp must be AFTER this run's start to confirm it's fresh.
            if (genMs >= runStartMs) {
              confirmedSuccess = true;
              toast.success(`Translated to ${target.toUpperCase()} (took longer than usual)`);
              onSaved && onSaved({
                languages: fresh.data?.languages || [],
                default_language: fresh.data?.default_language || 'en',
              });
              onTranslated && onTranslated(target);
              break;
            }
          }
        } catch (pollErr) {
          // eslint-disable-next-line no-console
          console.error(
            `[LanguagesPanel] overlay-confirmation poll attempt ${attempt + 1}/${POLL_ATTEMPTS} failed:`,
            pollErr,
          );
        }
      }

      if (!confirmedSuccess) {
        const niceMsg = status === 504
          ? 'Translation is taking longer than usual. It may still be processing in the background — refresh in a minute and check.'
          : (status ? `Translation failed (${status}): ${msg}` : `Translation failed: ${msg}`);
        toast.error(niceMsg, { duration: 8000 });
      }
    } finally {
      setTranslatingLang(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="languages-modal"
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1">
              <Globe className="h-3.5 w-3.5" />
              <span>Languages</span>
            </div>
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100">
              Make your invitation multilingual
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Guests will see their browser language automatically, or pick from a dropdown.
            </p>
            {/* ─── Translations Health badge ───────────────────
                Detects legacy structural pollution in stored
                language overlays (the layout-drift bug). Shows
                Healthy when clean; offers a one-click cleanup
                when stale fields are present. */}
            {health && (
              <div
                className={`mt-3 flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium ${
                  health.healthy
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
                }`}
                data-testid="translations-health-badge"
              >
                {health.healthy ? (
                  <>
                    <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Translations healthy</span>
                  </>
                ) : (
                  <>
                    <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="flex-1">
                      {health.total_stale_fields} stale layout field{health.total_stale_fields === 1 ? '' : 's'}
                      {health.languages_affected.length > 0 && (
                        <span className="opacity-75"> in {health.languages_affected.join(', ').toUpperCase()}</span>
                      )}
                    </span>
                    <button
                      onClick={handleCleanTranslations}
                      disabled={cleaning}
                      className="ml-1 px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                      data-testid="translations-health-clean-btn"
                    >
                      {cleaning && <Loader2 className="h-3 w-3 animate-spin" />}
                      Clean
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition flex-shrink-0 ml-3"
            data-testid="languages-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Language list */}
        <div className="p-6 space-y-2 max-h-[60vh] overflow-y-auto">
          {LANGUAGE_OPTIONS.map((opt) => {
            const isOn = enabled.has(opt.code);
            const isDefault = defLang === opt.code;
            const hasOverlay = !!hasTranslation[opt.code];
            const translating = translatingLang === opt.code;
            // Phase Lang-A.1 — staleness check. Stale when content was edited
            // after the overlay was generated. If the overlay has no generated_at
            // (pre-Lang-A.1 overlays), treat as stale so the user is prompted to
            // refresh once.
            const genAt = translationGeneratedAt[opt.code] || null;
            const isStale = hasOverlay && !isDefault && (
              !genAt || (contentUpdatedAt && new Date(contentUpdatedAt) > new Date(genAt))
            );
            return (
              <div
                key={opt.code}
                className={`flex items-center justify-between p-3 rounded-xl border transition ${
                  isOn
                    ? 'border-amber-400/60 bg-amber-50/40 dark:bg-amber-500/5'
                    : 'border-slate-200 dark:border-slate-800'
                }`}
                data-testid={`lang-row-${opt.code}`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleLang(opt.code)}
                    className={`h-5 w-5 rounded border flex items-center justify-center transition ${
                      isOn
                        ? 'bg-amber-500 border-amber-500 text-white'
                        : 'border-slate-300 dark:border-slate-700 bg-transparent'
                    }`}
                    data-testid={`lang-toggle-${opt.code}`}
                    aria-label={isOn ? 'Disable' : 'Enable'}
                  >
                    {isOn && <Check className="h-3 w-3" />}
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{opt.flag}</span>
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {opt.name}
                      </div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                        {opt.code}
                        {isDefault && <span className="ml-2 text-amber-600">default</span>}
                        {hasOverlay && !isDefault && !isStale && (
                          <span className="ml-2 text-emerald-600">up to date</span>
                        )}
                        {isStale && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"
                            data-testid={`lang-stale-${opt.code}`}
                          >
                            <span
                              className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"
                              aria-hidden="true"
                            />
                            needs refresh
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isOn && !isDefault && (
                    <button
                      onClick={() => setDefLang(opt.code)}
                      className="text-[11px] text-slate-500 hover:text-amber-600 transition"
                      data-testid={`lang-set-default-${opt.code}`}
                    >
                      Make default
                    </button>
                  )}
                  {isOn && !isDefault && (
                    <button
                      onClick={() => handleTranslate(opt.code)}
                      disabled={translating}
                      className={`inline-flex items-center gap-1 text-[12px] font-medium transition disabled:opacity-50 ${
                        isStale
                          ? 'px-2.5 py-1 rounded-md bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
                          : 'text-amber-700 dark:text-amber-400 hover:opacity-80'
                      }`}
                      data-testid={`lang-translate-${opt.code}`}
                    >
                      {translating ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" /> translating…
                        </>
                      ) : isStale ? (
                        <>
                          <Sparkles className="h-3 w-3" /> Refresh translation
                        </>
                      ) : hasOverlay ? (
                        <>
                          <Sparkles className="h-3 w-3" /> re-translate
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-3 w-3" /> auto-translate
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
          <p className="text-xs text-slate-500">
            Build the invitation in <strong>{defLang.toUpperCase()}</strong>, then auto-translate.
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
            data-testid="languages-save-btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
